import {
    Alert,
    Box,
    Button,
    IconButton,
    InputAdornment,
    InputLabel,
    Modal,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import { modalStyles, useStyles } from "./ChangePasswordModal.styles";
import { useTranslation } from "react-i18next";
import { IChangePasswordModal, IChangePasswordValues } from "./ChangePasswordModalInterfaces";
import { useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { changePasswordFormValidationSchema } from "validations/schemas";
import { changeUserPassword } from "api/queries/usersAPI";
import {
    CHANGE_PASSWORD_ERROR_CODES,
    CORPORATE_BACKOFFICE_PATHS,
    LocalStorageGlobalKeys,
    ServerResponseErrorCodes,
} from "constants/constants";
import { removeUser, resetLocalStorage } from "utilities/storageUtilities";
import { logout } from "api/queries/loginAPI";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ChangePasswordModal = ({ open, onClose }: IChangePasswordModal) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const initFormValues: IChangePasswordValues = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    };

    const handleChangePasswordSubmit = async (
        values: IChangePasswordValues,
        formikHelpers: FormikHelpers<IChangePasswordValues>,
    ) => {
        setIsSaving(true);
        try {
            await changeUserPassword(values);
            setShowSnackbar(true);
            await new Promise((resolve) => setTimeout(resolve, 5000));
            handleLogout();
        } catch (error: any) {
            const { response } = error;
            if (
                response &&
                response.status === ServerResponseErrorCodes.BAD_REQUEST &&
                response.data.errorCode &&
                response.data.errorCode === CHANGE_PASSWORD_ERROR_CODES.PASSWORD_INVALID
            ) {
                formikHelpers.setFieldError("newPassword", t("field_errors.password_validations"));
            }
            if (
                response &&
                response.status === ServerResponseErrorCodes.UNAUTHORIZED &&
                response.data.errorCode &&
                response.data.errorCode === CHANGE_PASSWORD_ERROR_CODES.PASSWORD_INCORRECT
            ) {
                formikHelpers.setFieldError("currentPassword", t("field_errors.current_password_incorrect"));
            }
            setIsSaving(false);
        }
    };

    const handleLogout = () => {
        removeUser();
        logout();
        resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);
        navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN);
    };

    return (
        <Modal open={open} onClose={onClose} disableEscapeKeyDown>
            <Box sx={modalStyles}>
                <div className={classes.modalHeader}>
                    {t(
                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.title",
                    )}
                </div>
                <div className={classes.modalBody}>
                    <Alert severity="warning" sx={{ width: "100%" }}>
                        {t(
                            "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.change_password_alert",
                        )}
                    </Alert>
                    <Formik
                        initialValues={initFormValues}
                        validationSchema={changePasswordFormValidationSchema}
                        onSubmit={handleChangePasswordSubmit}
                    >
                        {({ errors, touched, values, handleChange }) => {
                            return (
                                <Form className={classes.formContainer}>
                                    <>
                                        <Box className={classes.boxContainer}>
                                            <InputLabel id="current-password-label">
                                                <Typography variant="subtitle2" className={classes.formLabel}>
                                                    {t(
                                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.current_password",
                                                    )}
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                aria-labelledby="current-password-label"
                                                fullWidth
                                                id="current-password-input"
                                                className={classes.textField}
                                                name="currentPassword"
                                                type={showCurrentPassword ? "text" : "password"}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() =>
                                                                    setShowCurrentPassword(!showCurrentPassword)
                                                                }
                                                                onMouseDown={(event) => event.preventDefault()}
                                                                edge="end"
                                                            >
                                                                {showCurrentPassword ? (
                                                                    <VisibilityOff />
                                                                ) : (
                                                                    <Visibility />
                                                                )}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                variant="outlined"
                                                disabled={isSaving}
                                                autoFocus={true}
                                                value={values.currentPassword}
                                                onChange={handleChange}
                                                error={touched.currentPassword && Boolean(errors.currentPassword)}
                                                helperText={
                                                    touched.currentPassword &&
                                                    errors.currentPassword &&
                                                    t(errors.currentPassword)
                                                }
                                            />
                                        </Box>
                                        <Box className={classes.boxContainer}>
                                            <InputLabel id="new-password-label">
                                                <Typography variant="subtitle2" className={classes.formLabel}>
                                                    {t(
                                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.new_password",
                                                    )}
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                aria-labelledby="new-password-label"
                                                fullWidth
                                                id="new-password-input"
                                                className={classes.textField}
                                                name="newPassword"
                                                type={showNewPassword ? "text" : "password"}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                                onMouseDown={(event) => event.preventDefault()}
                                                                edge="end"
                                                            >
                                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                disabled={isSaving}
                                                variant="outlined"
                                                autoFocus={true}
                                                value={values.newPassword}
                                                onChange={handleChange}
                                                error={touched.newPassword && Boolean(errors.newPassword)}
                                                helperText={
                                                    touched.newPassword && errors.newPassword && t(errors.newPassword)
                                                }
                                            />
                                        </Box>
                                        <Box className={classes.boxContainer}>
                                            <InputLabel id="current-password-label">
                                                <Typography variant="subtitle2" className={classes.formLabel}>
                                                    {t(
                                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.confirm_new_password",
                                                    )}
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                aria-labelledby="confirm-password-label"
                                                fullWidth
                                                id="confirm-password-input"
                                                className={classes.textField}
                                                name="confirmPassword"
                                                disabled={isSaving}
                                                type={showConfirmPassword ? "text" : "password"}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() =>
                                                                    setShowConfirmPassword(!showConfirmPassword)
                                                                }
                                                                onMouseDown={(event) => event.preventDefault()}
                                                                edge="end"
                                                            >
                                                                {showConfirmPassword ? (
                                                                    <VisibilityOff />
                                                                ) : (
                                                                    <Visibility />
                                                                )}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                variant="outlined"
                                                autoFocus={true}
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                                                helperText={
                                                    touched.confirmPassword &&
                                                    errors.confirmPassword &&
                                                    t(errors.confirmPassword)
                                                }
                                            />
                                        </Box>
                                        <div className={classes.footer}>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                type="submit"
                                                className={classes.submitButton}
                                                disabled={isSaving}
                                            >
                                                {t(
                                                    "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.change_password_button",
                                                )}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="info"
                                                className={classes.submitButton}
                                                onClick={onClose}
                                                disabled={isSaving}
                                            >
                                                {t(
                                                    "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.close_button",
                                                )}
                                            </Button>
                                        </div>
                                    </>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>

                <Snackbar
                    open={showSnackbar}
                    autoHideDuration={6000}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Alert severity="success" sx={{ width: "100%" }}>
                        {t(
                            "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password_modal.password_changed_successfully",
                        )}
                    </Alert>
                </Snackbar>
            </Box>
        </Modal>
    );
};

export default ChangePasswordModal;
